import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader";
import {
  Badge,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { speechConstant } from "./constants";
import Button from "../../components/Button";
import { textToSpeech } from "../../store/quiz/quizBaseApi";
import quizPlayIcon from "../../assets/images/quiz/speech-play.svg";
import quizPauseIcon from "../../assets/images/quiz/speech-pause.svg";
import quizReplayIcon from "../../assets/images/quiz/speech-retry.svg";
import speechLoadingIcon from "../../assets/images/quiz/speech-loading.gif";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSpeechConfig } from "./hooks";
import { uploadJson } from "../../store/speechConfig/speechConfigBaseApi";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@emotion/react";
import { json_files } from "../../utils";

const useStyles = makeStyles()((theme) => ({
  title: {
    margin: "0",
    fontWeight: "700",
    lineHeight: "1.5",
    fontSize: "1.8rem",
  },
  headerBox: {
    background: theme.palette.primary.blue,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  headerText: {
    margin: 0,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: "1.5rem",
    color: "#fff",
    textAlign: "center",
  },
  label: {
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  inputLabel: {
    color: theme.palette.primary.darkGrey,
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    fontSize: "14px",
  },
  gridContainer: {
    marginTop: theme.spacing(2),
  },
  audioBox: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "space-evenly",
    alignItems: "center",
    padding: theme.spacing(2),
    border: "1px solid #ccc",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
}));
export const Edit = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { id } = useParams();
  const { loadSpeechConfig, speechConfigList, save } = useSpeechConfig();
  const [loading, setLoading] = useState(true);
  const [speechConfig, setSpeechConfig] = useState([]);
  const [geographyConfig, setGeographyConfig] = useState();
  const [geographyName, setGeographyName] = useState(null);
  const [audioStates, setAudioStates] = useState({});
  const audioRefs = React.useRef({});
  const navigate = useNavigate();
  const isInitialized = React.useRef(false);

  useEffect(() => {
    loadSpeechConfig();
  }, []);

  useEffect(() => {
    if (id && speechConfigList) {
      setSpeechConfig(speechConfigList);
      const currentSpeechConfig = speechConfigList.find(
        (speech) => speech.id === id,
      );
      setGeographyConfig(currentSpeechConfig);
      setGeographyName(currentSpeechConfig?.geographicalType);
    }
  }, [id, speechConfigList]);

  useEffect(() => {
    if (geographyConfig && !isInitialized.current) {
      const initialSentence =
        speechConstant.SAMPLE_SENTENCES[0] || "Default sample sentence";
      const initialAudioStates = {};

      (geographyConfig.learningGeneration || []).forEach((gen) => {
        initialAudioStates[gen.genId] = {
          currentSentence: initialSentence,
          isLoading: false,
          audioUrl: null,
          isPlaying: false,
        };
      });

      setAudioStates(initialAudioStates);

      // Load audio for each generation initially
      geographyConfig.learningGeneration.forEach((gen) => {
        loadAudio(initialSentence, gen, gen.genId);
      });

      isInitialized.current = true;
      setLoading(false);
    }
  }, [geographyConfig]);

  const loadAudio = async (sentence, generationConfig, genId) => {
    setAudioStates((prevState) => ({
      ...prevState,
      [genId]: {
        isLoading: true,
        audioUrl: prevState[genId]?.audioUrl || null,
        isPlaying: false,
        currentSentence: sentence,
      },
    }));
    try {
      const conf = {
        accent: generationConfig.accent,
        gender: generationConfig.gender,
        words_per_minute: generationConfig.words_per_minute,
      };
      const data = await textToSpeech({ text: sentence, ...conf });

      if (data && data.response && data.response.audio) {
        // new s3 endpoint change --- minimal fix for audio play
        const correctedAudioUrl = data.response.audio.replace(
          "knowledgebasechat.s3.eu-west-1.amazonaws.com",
          "knowledgebasechat.s3-us-west-1.amazonaws.com",
        );
        setAudioStates((prevState) => ({
          ...prevState,
          [genId]: {
            ...prevState[genId],
            audioUrl: correctedAudioUrl,
            isLoading: false,
            currentSentence: sentence,
          },
        }));

        // setAudioStates((prevState) => ({
        //     ...prevState,
        //     [genId]: { ...prevState[genId], audioUrl: data.response.audio, isLoading: false, currentSentence: sentence },
        // }));
      } else {
        toast.error("Failed to load audio.");
      }
    } catch (error) {
      const message = error.response?.data?.info?.detail || error.message;
      toast.error(message);
    } finally {
      setAudioStates((prevState) => ({
        ...prevState,
        [genId]: { ...prevState[genId], isLoading: false },
      }));
    }
  };

  const togglePlayPause = (genId) => {
    const audio = audioRefs.current[genId];
    if (audio) {
      if (audioStates[genId]?.isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
    }
  };

  const handlePlay = (genId) => {
    setAudioStates((prevState) => ({
      ...prevState,
      [genId]: { ...prevState[genId], isPlaying: true },
    }));
  };

  const handlePause = (genId) => {
    setAudioStates((prevState) => ({
      ...prevState,
      [genId]: { ...prevState[genId], isPlaying: false },
    }));
  };

  const handleEnd = (genId) => {
    setAudioStates((prevState) => ({
      ...prevState,
      [genId]: { ...prevState[genId], isPlaying: false },
    }));
  };

  const shuffleSentence = (genId) => {
    const randomIndex = Math.floor(
      Math.random() * speechConstant.SAMPLE_SENTENCES.length,
    );
    const newSentence = speechConstant.SAMPLE_SENTENCES[randomIndex];
    setAudioStates((prevState) => ({
      ...prevState,
      [genId]: { ...prevState[genId], currentSentence: newSentence },
    }));

    const updatedGen = geographyConfig?.learningGeneration?.find(
      (gen) => gen.genId === genId,
    );

    if (updatedGen) {
      loadAudio(newSentence, updatedGen, genId);
    }
  };

  const handleChange = (e, genId) => {
    const { name, value } = e.target;

    setGeographyConfig((prevConfig) => {
      if (!prevConfig) return prevConfig;

      const updatedLearningGeneration = prevConfig.learningGeneration.map(
        (gen) => (gen.genId === genId ? { ...gen, [name]: value } : gen),
      );

      const newConfig = {
        ...prevConfig,
        learningGeneration: updatedLearningGeneration,
      };

      const updatedGen = updatedLearningGeneration.find(
        (gen) => gen.genId === genId,
      );
      if (updatedGen) {
        loadAudio(audioStates[genId]?.currentSentence, updatedGen, genId);
      }

      return newConfig;
    });
  };

  const saveConfig = async () => {
    let data = { ...geographyConfig };
    let updatedData = [...speechConfig];
    let tempData = speechConfig.map((item) => {
      if (id === item.id) {
        return data;
      } else {
        return item;
      }
    });
    updatedData = tempData;

    const json = JSON.stringify(updatedData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const formData = new FormData();
    formData.append("file", blob, json_files.speechConfig.name);
    try {
      const resp = await uploadJson({ id, formData });

      if (resp.message) {
        toast.success(`Speech Configuration Update Success`);
        await new Promise((resolve) => setTimeout(resolve, 500));
        loadSpeechConfig();
        navigate("/speech-config");
      }
    } catch (e) {
      toast.error(e.error);
      return;
    }

    if (id) {
      data = { ...geographyConfig, id };
      save({ speech: data, navigate });
    }
    navigate("/speech-config");
  };

  const PlayIcon = () => (
    <img style={{ marginRight: "1rem" }} src={quizPlayIcon} />
  );
  const PauseIcon = () => (
    <img style={{ marginRight: "1rem" }} src={quizPauseIcon} />
  );
  const RetryIcon = () => (
    <img style={{ marginRight: "1rem" }} src={quizReplayIcon} />
  );
  const LoadingIcon = () => (
    <img
      style={{ marginRight: "1rem" }}
      height={"50px"}
      src={speechLoadingIcon}
    />
  );
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box>
          <Typography
            className={classes.title}
            align="left"
            variant="h1"
            color="primary.blue"
          >
            Edit Configuration{" "}
          </Typography>
          <Box>
            <Box className={classes.headerBox}>
              <Typography className={classes.headerText}>
                Geographical Type - {geographyName && geographyName}
              </Typography>
            </Box>
            {geographyConfig &&
              geographyConfig?.learningGeneration?.map((gen, i) => {
                const audioState = audioStates[gen.genId] || {};
                return (
                  <Grid
                    className={classes.gridContainer}
                    key={gen?.genId}
                    container
                    spacing={2}
                    alignItems="center"
                  >
                    <Grid item xs={12}>
                      <Typography className={classes.label}>
                        Learning Generation - {gen?.generation}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <InputLabel className={classes.inputLabel}>
                        Gender
                      </InputLabel>
                      <FormControl fullWidth>
                        <Select
                          name="gender"
                          value={gen?.gender || ""}
                          onChange={(e) => handleChange(e, gen?.genId)}
                          placeholder="gender"
                          sx={{ height: "47px" }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {Object.entries(speechConstant.GENDER).map(
                            ([key, value], i) => {
                              return (
                                <MenuItem key={i} value={key}>
                                  {value}
                                </MenuItem>
                              );
                            },
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <InputLabel className={classes.inputLabel}>
                        Accent{" "}
                      </InputLabel>
                      <FormControl fullWidth>
                        <Select
                          name="accent"
                          value={gen?.accent || ""}
                          onChange={(e) => handleChange(e, gen?.genId)}
                          placeholder="Accent"
                          sx={{ height: "47px" }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {Object.entries(speechConstant.ACCENT).map(
                            ([key, value], i) => {
                              return (
                                <MenuItem key={i} value={key}>
                                  {value}
                                </MenuItem>
                              );
                            },
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <InputLabel className={classes.inputLabel}>
                        Words Per Minute{" "}
                      </InputLabel>
                      <FormControl fullWidth>
                        <Select
                          name="words_per_minute"
                          value={gen?.words_per_minute || ""}
                          onChange={(e) => handleChange(e, gen?.genId)}
                          placeholder="Words Per Minute"
                          sx={{ height: "47px" }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {speechConstant.WORDS_PER_MIN.map((min, i) => {
                            return (
                              <MenuItem key={i} value={min}>
                                {min}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <InputLabel className={classes.inputLabel}>
                        Hear Sample !!
                      </InputLabel>
                      <Box className={classes.audioBox}>
                        <Tooltip
                          title={
                            audioState.isLoading
                              ? "Loading"
                              : audioState.audioUrl
                                ? audioState.isPlaying
                                  ? "Pause"
                                  : "Play"
                                : "Retry"
                          }
                        >
                          <Badge>
                            <Button
                              endIcon={
                                audioState.isLoading ? (
                                  <LoadingIcon />
                                ) : audioState.audioUrl ? (
                                  audioState.isPlaying ? (
                                    <PauseIcon />
                                  ) : (
                                    <PlayIcon />
                                  )
                                ) : (
                                  <RetryIcon />
                                )
                              }
                              size="small"
                              type="secondary"
                              onClick={() => {
                                if (!audioState.audioUrl) {
                                  loadAudio(
                                    audioState.currentSentence,
                                    gen,
                                    gen.genId,
                                  );
                                } else {
                                  togglePlayPause(gen.genId);
                                }
                              }}
                              // disabled={isLoading || !audioUrl}
                            />
                          </Badge>
                        </Tooltip>
                        <Typography>{audioState.currentSentence}</Typography>

                        <Tooltip title="Shuffle">
                          <Badge>
                            <Button
                              size="large"
                              onClick={() => shuffleSentence(gen.genId)}
                              placeholder={<RetryIcon />}
                            />
                          </Badge>
                        </Tooltip>
                        <audio
                          ref={(ref) => (audioRefs.current[gen.genId] = ref)}
                          src={audioState.audioUrl}
                          onPlay={() => handlePlay(gen.genId)}
                          onPause={() => handlePause(gen.genId)}
                          onEnded={() => handleEnd(gen.genId)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
          </Box>
          <Box className={classes.saveButton}>
            <Button
              size="large"
              variant="contained"
              onClick={saveConfig}
              color="primary"
              placeholder={"Save Configuration"}
            />
          </Box>
        </Box>
      )}
    </>
  );
};
